import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from "@ngrx/store";
import {
  DOCUMENT_FEATURE_KEY,
  DocumentState,
  DocumentPartialState,
  documentAdapter,
  UploadStatus,
} from "./document.reducer";

export const getDocumentState = createFeatureSelector<
  DocumentPartialState,
  DocumentState
>(DOCUMENT_FEATURE_KEY);

const { selectAll, selectEntities } = documentAdapter.getSelectors();

export const getDocumentRecordLoaded = createSelector(
  getDocumentState,
  (state: DocumentState) => state.loaded,
);

export const getDocumentRecordError = createSelector(
  getDocumentState,
  (state: DocumentState) => state.error,
);

export const loadingDocumentRecord = createSelector(
  getDocumentState,
  (state: DocumentState) => state.loaded,
);

export const getAllDocumentRecords = createSelector(
  getDocumentState,
  (state: DocumentState) => selectAll(state),
);

export const getDocumentRecordEntities = createSelector(
  getDocumentState,
  (state: DocumentState) => selectEntities(state),
);

export const getSelectedId = createSelector(
  getDocumentState,
  (state: DocumentState) => state.selectedId,
);

export const getSelected = createSelector(
  getDocumentRecordEntities,
  getSelectedId,
  (entities, selectedId) => selectedId && entities[selectedId],
);

const getError = (state: DocumentState): string => state.error;

const getStarted = (state: DocumentState): boolean =>
  state.status === UploadStatus.Started;

const getRequested = (state: DocumentState): boolean =>
  state.status === UploadStatus.Requested;

const getReady = (state: DocumentState): boolean =>
  state.status === UploadStatus.Ready;

const getProgress = (state: DocumentState): number => state.progress;

const getInProgress = (state: DocumentState): boolean =>
  state.status === UploadStatus.Started && state.progress >= 0;

const getFailed = (state: DocumentState): boolean =>
  state.status === UploadStatus.Failed;

const getCompleted = (state: DocumentState): boolean =>
  state.status === UploadStatus.Completed;

export const selectUploadFileError: MemoizedSelector<
  object,
  string
> = createSelector(
  getDocumentState,
  getError,
);

export const selectUploadFileReady: MemoizedSelector<
  object,
  boolean
> = createSelector(
  getDocumentState,
  getReady,
);

export const selectUploadFileRequested: MemoizedSelector<
  object,
  boolean
> = createSelector(
  getDocumentState,
  getRequested,
);

export const selectUploadFileStarted: MemoizedSelector<
  object,
  boolean
> = createSelector(
  getDocumentState,
  getStarted,
);

export const selectUploadFileProgress: MemoizedSelector<
  object,
  number
> = createSelector(
  getDocumentState,
  getProgress,
);

export const selectUploadFileInProgress: MemoizedSelector<
  object,
  boolean
> = createSelector(
  getDocumentState,
  getInProgress,
);

export const selectUploadFileFailed: MemoizedSelector<
  object,
  boolean
> = createSelector(
  getDocumentState,
  getFailed,
);

export const selectUploadFileCompleted: MemoizedSelector<
  object,
  boolean
> = createSelector(
  getDocumentState,
  getCompleted,
);
